import React, { useState } from "react";
import { Button, DropdownTrigger, Dropdown, DropdownMenu, DropdownItem, Modal, ModalContent, ModalBody, ModalFooter } from "@heroui/react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUser from "@/hooks/useUser";
import ArchiveModal from "../common/modal/ArchiveModal";
import UpdateEntityModal from "../common/modal/UpdateEntityModal";
import useLocalStorageList from "../../hooks/useLocalStorageList";

import { useTranslation } from "react-i18next";
import { VerticalDotsIcon } from "./VerticalDotsIcon";
import { FaRegTrashCan } from "react-icons/fa6";
import { GiProcessor } from "react-icons/gi";
import { IoReload } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { MdOutlineMarkAsUnread, MdOutlineMergeType, MdOutlineNoBackpack } from "react-icons/md";
import { useUseCases } from "@/context/UseCasesProvider";
import { isProduction } from "@/utils/environment";

interface SecondaryActionsProps {
  orderId: string;
  setUnreadOrders: React.Dispatch<React.SetStateAction<string[]>>;
}

interface UserData {
  user_email: string;
  allow_update_entities: boolean;
}

export default function SecondaryActions({
  orderId,
  setUnreadOrders,
}: Readonly<SecondaryActionsProps>): React.ReactElement {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const settingsInterface = useUseCases();
  const { addItem } = useLocalStorageList("unreadOrders", orderId);
  const { data: userData } = useUser();
  const [activeModal, setActiveModal] = useState<"archive" | "entity" | null>(null);
  const [confirmAction, setConfirmAction] = useState<'emailReprocess' | 'completeReprocess' | 'partialReprocess' | null>(null);

  const openModal = (modalType: "archive" | "entity") => {
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const handleMarkAsUnread = (): void => {
    addItem();

    setUnreadOrders((prevUnreadOrders) => {
      const updatedOrders = [...prevUnreadOrders, orderId];
      localStorage.setItem("unreadOrders", JSON.stringify(updatedOrders));
      return updatedOrders;
    });
  };

  const executeCompleteReprocess = async (): Promise<void> => {
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step`);
    } catch (error) {
      console.error(t("complete_reprocess_error"), error);
    }
  };

  const executePartialReprocess = async (): Promise<void> => {
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=skip_llm`);
    } catch (error) {
      console.error(t("partial_reprocess_error"), error);
    }
  };

  const executeEmailReprocess = async (): Promise<void> => {
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=from_email_reception`);
    } catch (error) {
      console.error(t("email_reprocess_error"), error);
    }
  };

  const handleConfirmReprocess = () => {
    if (confirmAction === 'emailReprocess') {
      executeEmailReprocess();
    } else if (confirmAction === 'completeReprocess') {
      executeCompleteReprocess();
    } else if (confirmAction === 'partialReprocess') {
      executePartialReprocess();
    }
  };

  const handleEmailReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('emailReprocess');
    } else {
      executeEmailReprocess();
    }
  };

  const handleCompleteReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('completeReprocess');
    } else {
      executeCompleteReprocess();
    }
  };

  const handlePartialReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('partialReprocess');
    } else {
      executePartialReprocess();
    }
  };

  const copyToDev = async (): Promise<void> => {
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=copy_to_dev`);
    } catch (error) {
      console.error(t("copy_to_dev_error"), error);
    }
  };

  const update_ack = async (): Promise<void> => {
    try {
      const data = [{
        jobID: orderId,
        ack_read: false,
      }];

      await axiosPrivate.put(`/orders/ack_read`, data);
    } catch (error) {
      console.error(t("update_ack_error"), error);
    }
  };

  const shouldDisplayActions: boolean = userData?.user_email?.includes("@vistaflow.ai") ?? false;
  const shouldUpdateEntities: boolean = userData?.allow_update_entities ?? false;

  return (
    <div className="relative flex justify-end items-center gap-2">
      <Dropdown className="bg-background border-1 border-default-200">
        <DropdownTrigger>
          <Button
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            data-cy={`order__secondaryActions-${orderId}`}
          >
            <VerticalDotsIcon className="text-default-400" width={24} height={24} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          <DropdownItem key="archive" onPress={() => openModal("archive")}>
            <div className="flex items-center gap-2">
              <FaRegTrashCan />
              {t("archive")}
            </div>
          </DropdownItem>
          {shouldUpdateEntities && settingsInterface?.settings?.display?.features?.entity ? (
            <DropdownItem
              key="entity"
              data-cy={`order__secondaryActions-updateEntity`}
              onPress={() => openModal("entity")}
            >
              <div className="flex items-center gap-2">
                <MdOutlineMergeType />
                {t("update_entity")}
              </div>
            </DropdownItem>
          ) : null}
          <DropdownItem key="mark-as-unread" onPress={handleMarkAsUnread}>
            <div className="flex items-center gap-2">
              <MdOutlineMarkAsUnread />
              {t("mark_as_unread")}
            </div>
          </DropdownItem>
           {settingsInterface?.settings.display.features.ack_update && (
            <DropdownItem key="update-ack" onPress={() => update_ack()}>
              <div className="flex items-center gap-2">
                <MdOutlineNoBackpack />
                {t("ack_update")}
              </div>
            </DropdownItem>
          )}
          {shouldDisplayActions ? (
            <DropdownItem key="email-reprocess" onPress={handleEmailReprocessClick}>
              <div className="flex items-center gap-2">
                <IoReload />
                {t("reprocess_from_email")}
              </div>
            </DropdownItem>
          ) : null}
          {shouldDisplayActions ? (
            <DropdownItem key="complete-reprocess" onPress={handleCompleteReprocessClick}>
              <div className="flex items-center gap-2">
                <GiProcessor />
                {t("complete_reprocess")}
              </div>
            </DropdownItem>
          ) : null}
          {shouldDisplayActions ? (
            <DropdownItem key="partial-reprocess" onPress={handlePartialReprocessClick}>
              <div className="flex items-center gap-2">
                <IoReload />
                {t("post_llm_reprocess")}
              </div>
            </DropdownItem>
          ) : null}
          {import.meta.env.VITE_ENV === "production" && shouldDisplayActions ? (
            <DropdownItem key="copy-to-dev" onPress={() => copyToDev()}>
              <div className="flex items-center gap-2">
                <LuCopyPlus />
                {t("copy_to_dev")}
              </div>
            </DropdownItem>
          ) : null}

        </DropdownMenu>
      </Dropdown>
      <ArchiveModal isOpen={activeModal === "archive"} onClose={closeModal} orderId={orderId} />
      <UpdateEntityModal
        isOpen={activeModal === "entity"}
        onClose={closeModal}
        classNames={""}
        orderId={orderId}
      />

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        backdrop="opaque"
        placement="center"
        size="md"
        classNames={{
          backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
        }}
      >
        <ModalContent>
          <ModalBody>
            <div className="pt-5 pb-3">
              <div className="text-xl font-bold pb-3">{t("confirm_reprocess") || "Confirm Reprocess"}</div>
              <div className="text-red-500 font-bold text-sm">
                {t("confirm_reprocess_message") || "Do you want to launch the reprocess?"}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="flat"
              onPress={() => setConfirmAction(null)}
              className="capitalize"
            >
              {t("no") || "No"}
            </Button>
            <Button
              className="bg-vistagreen hover:bg-vistagreen/90 text-white capitalize"
              onPress={handleConfirmReprocess}
            >
              {t("yes") || "Yes"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
